import classNames from "classnames";
import React from "react";
import { useLocalizedText } from "../../hooks/context-provider-hooks/use-localized-text";
import { Button, IconButton } from "../button";
import {
  BDS_PAGINATION_NEXT_PAGE_DEFAULT_TEXT,
  BDS_PAGINATION_PREVIOUS_PAGE_DEFAULT_TEXT,
  BDS_TEXT_TEMPLATE_KEYS,
} from "../constants";
import { IconNavArrowLeft, IconNavArrowRight } from "../icons";
import { Text } from "../text";
import { CSS_CLASSES } from "./pagination-css-classes";
import { PaginationColorOption } from "./pagination.types";

export interface PaginationButtonsProps {
  color?: PaginationColorOption;
  page: number;
  lastPage: number;
  onPageChange: (page: number) => void;
}

export const PaginationButtons: React.FC<PaginationButtonsProps> = ({ color, page, lastPage, onPageChange }) => {
  const localizedText = useLocalizedText(true);
  const pages = React.useMemo(() => {
    const pages = new Array(lastPage).fill(null).map((e, i) => i + 1);
    if (lastPage > 10) {
      if (page <= 8) {
        pages.splice(8, pages.length, null, lastPage);
      } else if (page > lastPage - 8) {
        pages.splice(0, pages.length - 8, 1, null);
      } else {
        pages.splice(page + 5, pages.length, null, lastPage);
        pages.splice(0, page - 1, 1, null);
      }
    }
    return pages;
  }, [page, lastPage]);

  return (
    <>
      <IconButton
        className={classNames(CSS_CLASSES.PAGINATION_ARROW_BUTTON, {
          [CSS_CLASSES.PAGINATION_ARROW_BUTTON_HIDDEN]: page === 1,
        })}
        onClick={() => onPageChange(page - 1)}
        label={
          localizedText
            ? localizedText(
                BDS_TEXT_TEMPLATE_KEYS.BDS_PAGINATION_PREVIOUS_PAGE,
                undefined,
                false,
                BDS_PAGINATION_PREVIOUS_PAGE_DEFAULT_TEXT
              )
            : BDS_PAGINATION_PREVIOUS_PAGE_DEFAULT_TEXT
        }
        variant="ghost"
        size="small"
        color={color}
        icon={<IconNavArrowLeft />}
      />
      <div className={CSS_CLASSES.PAGINATION_PAGE_BUTTONS}>
        {pages.map((pageIndex, i) =>
          pageIndex === null ? (
            <Text className={CSS_CLASSES.PAGINATION_ELLIPSIS} key={i} color={color} variant="label">
              ...
            </Text>
          ) : (
            <Button
              key={i}
              size="small"
              color={color}
              className={CSS_CLASSES.PAGINATION_PAGE_BUTTON}
              onClick={() => onPageChange(pageIndex)}
              variant={pageIndex === page ? "filled" : "ghost"}
              label={pageIndex.toString()}
            />
          )
        )}
      </div>
      <IconButton
        className={classNames(CSS_CLASSES.PAGINATION_ARROW_BUTTON, {
          [CSS_CLASSES.PAGINATION_ARROW_BUTTON_HIDDEN]: page === lastPage,
        })}
        onClick={() => onPageChange(page + 1)}
        label={
          localizedText
            ? localizedText(
                BDS_TEXT_TEMPLATE_KEYS.BDS_PAGINATION_NEXT_PAGE,
                undefined,
                false,
                BDS_PAGINATION_NEXT_PAGE_DEFAULT_TEXT
              )
            : BDS_PAGINATION_NEXT_PAGE_DEFAULT_TEXT
        }
        variant="ghost"
        size="small"
        color={color}
        icon={<IconNavArrowRight />}
      />
    </>
  );
};
